import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import CountUp from "react-countup";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { getKpisHistoricos } from "../../helpers/backend_helper";
import ModalHistoricalChart from './ModalHistoricalChart';

const HistoricalWidgets = ({ proveedorSeleccionado, aseguradora }) => {
    const [kpis, setKpis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState(null);

    // Determinar código de proveedor (props > sesion > null)
    const perfil = DatosSesion("perfil");
    const esAdministrador = perfil === "Administrador" || perfil === "MyHealth";
    const codigoProveedor = proveedorSeleccionado || 
                           (!esAdministrador ? (DatosSesion("farmacia") || DatosSesion("id")) : null);

    useEffect(() => {
        const fetchKpis = async () => {
            setLoading(true);
            try {
                const params = {};
                if (codigoProveedor && codigoProveedor !== 'GLOBAL') {
                    params.codigo_proveedor = codigoProveedor;
                }
                if (aseguradora) {
                    params.aseguradora = aseguradora;
                }

                const response = await getKpisHistoricos(params);
                setKpis(response.data?.data);
            } catch (error) {
                console.error("Error cargando KPIs históricos:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchKpis();
    }, [codigoProveedor, aseguradora]);

    const openModal = (metrica, titulo, color) => {
        setSelectedMetric({ key: metrica, title: titulo, color });
        setModalOpen(true);
    };

    if (loading && !kpis) {
        return (
            <>
                <Row className="mb-3">
                    <Col xs={12}>
                        <div className="d-flex align-items-center">
                            <h5 className="card-title mb-0 flex-grow-1 text-muted text-uppercase fs-13 placeholder-glow">
                                <span className="placeholder col-3"></span>
                            </h5>
                        </div>
                    </Col>
                </Row>
                <Row>
                    {[1, 2, 3, 4].map((item) => (
                        <Col xl={3} md={6} key={item}>
                            <Card className="card-animate overflow-hidden">
                                <CardBody>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1 overflow-hidden">
                                            <p className="text-uppercase fw-medium text-muted text-truncate mb-3 placeholder-glow">
                                                <span className="placeholder col-6"></span>
                                            </p>
                                            <h4 className="fs-22 fw-semibold ff-secondary mb-0 placeholder-glow">
                                                <span className="placeholder col-4"></span>
                                            </h4>
                                        </div>
                                        <div className="flex-shrink-0">
                                            <div className="avatar-sm placeholder-glow">
                                                <span className="placeholder col-12 h-100 w-100 rounded"></span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-3 placeholder-glow">
                                        <span className="placeholder col-5"></span>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </>
        );
    }

    const anio = kpis?.anio || new Date().getFullYear();

    const widgets = [
        {
            id: 1,
            label: "Total Facturado",
            value: kpis?.total_facturado_usd || 0,
            prefix: "$",
            suffix: "",
            decimals: 2,
            icon: "ri-money-dollar-circle-line",
            color: "success",
            metricaKey: "facturacion",
            subtitle: kpis?.facturas_pagadas ? `En ${kpis.facturas_pagadas} facturas pagadas` : null
        },
        {
            id: 2,
            label: "Total Facturas",
            value: kpis?.total_facturas || 0,
            icon: "ri-file-list-3-line",
            color: "primary",
            metricaKey: "solicitudes",
            subtitle: kpis?.total_solicitudes ? `En ${kpis.total_solicitudes} solicitudes` : null
        },
        {
            id: 3,
            label: "Calificación Promedio",
            value: kpis?.calificacion_promedio || 0,
            decimals: 1,
            icon: "ri-star-line",
            color: "warning",
            metricaKey: "calificacion",
            subtitle: "Basado en calificaciones del año"
        },
        {
            id: 4,
            label: "Total Envíos (Delivery)",
            value: kpis?.total_delivery || 0,
            icon: "ri-motorbike-line",
            color: "info",
            metricaKey: "delivery",
            subtitle: "Entregas realizadas en el año"
        }
    ];

    return (
        <>
            <Row className="mb-3">
                <Col xs={12}>
                    <div className="d-flex align-items-center">
                        <h5 className="card-title mb-0 flex-grow-1 text-muted text-uppercase fs-13">
                            <i className="ri-calendar-line me-1"></i> Métricas del Año {anio}
                        </h5>
                    </div>
                </Col>
            </Row>

            <Row>
                {widgets.map((widget) => (
                    <Col xl={3} md={6} key={widget.id}>
                        <Card 
                            className="card-animate overflow-hidden" 
                            style={{ cursor: 'pointer', borderLeft: `4px solid var(--vz-${widget.color})` }}
                            onClick={() => openModal(widget.metricaKey, widget.label, widget.color === 'warning' ? '#f7b84b' : undefined)}
                        >
                            <CardBody>
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1 overflow-hidden">
                                        <p className="text-uppercase fw-medium text-muted text-truncate mb-3">
                                            {widget.label}
                                        </p>
                                        <h4 className="fs-22 fw-semibold ff-secondary mb-0">
                                            {widget.prefix}
                                            <CountUp
                                                start={0}
                                                end={widget.value}
                                                decimals={widget.decimals || 0}
                                                duration={2}
                                                separator=","
                                            />
                                            {widget.suffix}
                                        </h4>
                                        {widget.subtitle && (
                                            <p className={`text-${widget.color} fs-12 fw-medium mb-0 mt-1`}>
                                                {widget.subtitle}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className={`avatar-sm`}>
                                            <span className={`avatar-title bg-${widget.color}-subtle text-${widget.color} rounded fs-3`}>
                                                <i className={widget.icon}></i>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <span className="text-muted fs-12">
                                        <i className="ri-line-chart-line me-1"></i>
                                        Click para ver tendencia
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            {selectedMetric && (
                <ModalHistoricalChart
                    isOpen={modalOpen}
                    toggle={() => setModalOpen(!modalOpen)}
                    metrica={selectedMetric.key}
                    titulo={selectedMetric.title}
                    color={selectedMetric.color}
                    codigoProveedor={codigoProveedor}
                    aseguradora={aseguradora}
                />
            )}
        </>
    );
};

export default HistoricalWidgets;
